import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Share } from "lucide-react";

/**
 * PWA install prompt.
 * - Android/Desktop Chromium: listens to `beforeinstallprompt`, shows a banner
 *   and triggers the native install flow.
 * - iOS Safari (no beforeinstallprompt support): shows a hint banner with
 *   instructions ("Share → Add to Home Screen") since Apple requires manual
 *   install.
 *
 * Dismissal is persisted in localStorage so we don't nag the user.
 */
export default function PwaInstallPrompt() {
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [visible, setVisible] = useState(false);
    const [isIos, setIsIos] = useState(false);

    useEffect(() => {
        const dismissed = localStorage.getItem("pwa_install_dismissed");
        const installed = window.matchMedia("(display-mode: standalone)").matches
            || window.navigator.standalone === true;
        if (installed) return;

        const ua = window.navigator.userAgent || "";
        const iosDevice = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
        const isStandaloneiOS = window.navigator.standalone === true;
        if (iosDevice && !isStandaloneiOS && !dismissed) {
            setIsIos(true);
            // small delay so it doesn't block first paint
            const t = setTimeout(() => setVisible(true), 4000);
            return () => clearTimeout(t);
        }

        const handler = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
            if (!dismissed) setVisible(true);
        };
        window.addEventListener("beforeinstallprompt", handler);
        const installedHandler = () => {
            setVisible(false);
            setDeferredPrompt(null);
        };
        window.addEventListener("appinstalled", installedHandler);
        return () => {
            window.removeEventListener("beforeinstallprompt", handler);
            window.removeEventListener("appinstalled", installedHandler);
        };
    }, []);

    const dismiss = () => {
        localStorage.setItem("pwa_install_dismissed", String(Date.now()));
        setVisible(false);
    };

    const install = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
            setVisible(false);
        }
        setDeferredPrompt(null);
    };

    if (!visible) return null;

    return (
        <div
            data-testid="pwa-install-banner"
            className="fixed left-3 right-3 bottom-3 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-50
                rounded-2xl bg-white dark:bg-slate-900 border border-border shadow-2xl p-3 flex items-start gap-3
                animate-in slide-in-from-bottom-4 duration-300"
        >
            <div className="w-10 h-10 rounded-xl bg-discover-gradient flex items-center justify-center flex-shrink-0">
                <Download className="text-white" size={20} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="font-black text-sm leading-tight">
                    Installer Discoveur
                </div>
                {isIos ? (
                    <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                        Touche <Share size={11} className="inline -mt-0.5" /> puis
                        « Sur l'écran d'accueil » pour ajouter Discoveur à ton iPhone.
                    </p>
                ) : (
                    <p className="text-[11px] text-muted-foreground mt-1 leading-snug">
                        Accède à Discoveur depuis ton écran d'accueil, comme une vraie app.
                    </p>
                )}
                {!isIos && (
                    <Button
                        size="sm"
                        onClick={install}
                        data-testid="pwa-install-btn"
                        className="mt-2 h-8 rounded-full bg-discover-gradient text-white text-xs font-bold px-4"
                    >
                        Installer
                    </Button>
                )}
            </div>
            <button
                onClick={dismiss}
                data-testid="pwa-install-dismiss"
                aria-label="Fermer"
                className="text-muted-foreground hover:text-foreground p-1 -mt-1 -mr-1"
            >
                <X size={16} />
            </button>
        </div>
    );
}
